(() => {
  'use strict';
  // Locale strings from layout/theme.liquid (window.abrReviewsStrings); English when missing.
  const abrText = (key, english, values) => {
    const strings = window.abrReviewsStrings || {};
    let text = typeof strings[key] === 'string' && strings[key] ? strings[key] : english;
    if (values) Object.keys(values).forEach((name) => { text = text.split(`[${name}]`).join(String(values[name])); });
    return text;
  };

  /*
   * A set number of written reviews on every page.
   *
   * Judge.me pages its reviews 5 at a time, including ones that are only a
   * star rating with no text, which the widget hides - so a page could
   * show anywhere from 0 to 5 reviews and the section kept resizing. Here
   * every review is fetched once (from the same endpoint Judge.me's own
   * page links use), and the list is then paged in the browser: written
   * reviews only, PAGE_SIZE per page, with sorting, the star filter and
   * "Verified only" applied to the whole set.
   *
   * Strictly additive: if anything about the fetch isn't as expected (no
   * context, an error, a response that isn't this product's reviews), it
   * does nothing and Judge.me's own paging stays in charge.
   */

  if (window.__adobeReviewsAllV1) return;
  window.__adobeReviewsAllV1 = true;

  const PAGE_SIZE = 5;
  const REQUEST_SIZE = 50;
  const MAX_REQUESTS = 30;
  const TIMEOUT = 8000;
  const context = window.abrReviewsContext || {};

  let reviews = null; // [{ node, id, rating, verified, time, index }]
  let takenOver = false;
  let loading = false;
  let page = 1;
  let ratingFilter = null;

  const widget = () => document.querySelector('.template-product .jdgm-review-widget');
  const liveRoot = () => window.AdobeReviewsData?.liveRoot(widget()) || widget()?.querySelector('.jdgm-rev-widg');

  function hasText(review) {
    const clean = (value) => String(value || '').replace(/[\s\u200B-\u200F\u2060\uFEFF]/g, '');
    return Boolean(clean(review.querySelector('.jdgm-rev__title')?.textContent) || clean(review.querySelector('.jdgm-rev__body')?.textContent));
  }

  // The list shows a review with words or with the reviewer's own photos
  // (or video); only a bare star rating stays out of it.
  function hasMedia(review) {
    return Boolean(review.querySelector('.jdgm-rev__pics img, .jdgm-rev__vids :is(video, iframe)'));
  }
  const shown = (review) => hasText(review) || hasMedia(review);

  // Judge.me's markup can hold a photo's address in data-src until its own
  // script loads it, which never happens for the copies made here.
  function wakeMedia(review) {
    review.querySelectorAll('.jdgm-rev__pics img, .jdgm-rev__vids :is(img, video, iframe, source)').forEach((media) => {
      if (media instanceof HTMLImageElement) media.loading = 'lazy';
      const current = media.getAttribute('src');
      if (media.dataset.src && (!current || current.startsWith('data:'))) media.setAttribute('src', media.dataset.src);
      if (media.dataset.srcset && !media.getAttribute('srcset')) media.setAttribute('srcset', media.dataset.srcset);
    });
  }

  // Judge.me timestamps look like "2024-03-01 12:00:00 UTC", which not
  // every browser parses; make it ISO first.
  function parseTime(value) {
    const raw = String(value || '').trim();
    if (!raw) return 0;
    return Date.parse(raw) || Date.parse(raw.replace(' UTC', 'Z').replace(' ', 'T')) || 0;
  }

  function describe(node, index) {
    const ratingEl = node.querySelector('.jdgm-rev__rating');
    const rating = Number.parseFloat(ratingEl?.dataset.score || ratingEl?.getAttribute('aria-label')) || 0;
    const stamp = node.querySelector('.jdgm-rev__timestamp');
    const time = parseTime(stamp?.dataset.content || stamp?.getAttribute('datetime'));
    return {
      node,
      id: node.dataset.reviewId || node.getAttribute('data-review-id') || '',
      rating,
      verified: node.dataset.verifiedBuyer === 'true',
      time,
      index
    };
  }

  // ---- Fetching -----------------------------------------------------------

  function hosts() {
    const list = [window.jdgm?.API_HOST, 'https://judge.me', 'https://api.judge.me']
      .filter(Boolean)
      .map((host) => String(host).replace(/\/$/, ''))
      .map((host) => (host.startsWith('http') ? host : `https://${host}`));
    return Array.from(new Set(list));
  }

  async function request(host, pageNumber) {
    const params = new URLSearchParams({
      url: context.shop,
      shop_domain: context.shop,
      platform: 'shopify',
      page: String(pageNumber),
      per_page: String(REQUEST_SIZE),
      product_id: String(context.productId)
    });
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT);
    try {
      const response = await fetch(`${host}/reviews/reviews_for_widget?${params}`, { signal: controller.signal, credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      let html = text;
      try {
        const json = JSON.parse(text);
        html = json.html || json.reviews || '';
      } catch (error) { /* plain HTML */ }
      const template = document.createElement('template');
      template.innerHTML = String(html);
      return Array.from(template.content.querySelectorAll('.jdgm-rev[data-review-id]'));
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function fetchAll(total, knownIds) {
    let host = null;
    let first = null;
    for (const candidate of hosts()) {
      try {
        const nodes = await request(candidate, 1);
        // Only trust a response carrying reviews already on the page.
        if (nodes.some((node) => knownIds.has(node.dataset.reviewId))) {
          host = candidate;
          first = nodes;
          break;
        }
      } catch (error) { /* next host */ }
    }
    if (!host) return null;

    const perPage = first.length || 1;
    const pages = Math.min(MAX_REQUESTS, Math.ceil(total / perPage));
    const rest = [];
    for (let n = 2; n <= pages; n += 1) rest.push(n);
    const results = [first];
    // A few at a time.
    for (let i = 0; i < rest.length; i += 6) {
      const batch = await Promise.all(rest.slice(i, i + 6).map((n) => request(host, n)));
      results.push(...batch);
    }

    const seen = new Set();
    const all = [];
    results.flat().forEach((node) => {
      const id = node.dataset.reviewId;
      if (!id || seen.has(id)) return;
      seen.add(id);
      node.querySelectorAll('.jdgm-rev__pics img, .jdgm-rev__vids img').forEach((image) => image.loading = 'lazy');
      all.push(node);
    });
    return all;
  }

  // ---- Building reviews from the endpoint ---------------------------------
  //
  // The endpoint returns Judge.me's raw review markup, which its own script
  // finishes on the page (layout, stars, dates, badge). Rather than try to
  // redo that, each fetched review is built from a clone of a review
  // Judge.me has already finished on the page - so every review looks
  // exactly like the ones it renders itself - with this review's own
  // text, name, rating, date, verified status and media put in.

  function templateFrom(root) {
    const done = Array.from(root.querySelectorAll('.jdgm-rev[data-review-id]')).filter((review) =>
      review.querySelector('.jdgm-rev__title') && review.querySelector('.jdgm-rev__body') && review.querySelector('.jdgm-rev__author')
    );
    const plain = (review) => !review.querySelector('.jdgm-rev__pics img, .jdgm-rev__vids video, .jdgm-rev__vids iframe');
    const base = done.find((review) => plain(review) && review.dataset.verifiedBuyer === 'true') || done.find(plain) || done[0];
    return base ? base.cloneNode(true) : null;
  }

  let relativeTimeFormatter;
  function relativeTime(time) {
    if (!time) return '';
    const days = Math.round((Date.now() - time) / 86400000);
    const format = relativeTimeFormatter ||= new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (days >= 365) return format.format(-Math.round(days / 365), 'year');
    if (days >= 30) return format.format(-Math.round(days / 30), 'month');
    if (days >= 7) return format.format(-Math.round(days / 7), 'week');
    return format.format(-Math.max(0, days), 'day');
  }

  function build(raw, template) {
    const node = template.cloneNode(true);
    Array.from(raw.attributes).forEach((attribute) => {
      if (attribute.name.startsWith('data-')) node.setAttribute(attribute.name, attribute.value);
    });
    const verified = raw.hasAttribute('data-verified-buyer')
      ? raw.dataset.verifiedBuyer === 'true'
      : Boolean(raw.querySelector('.jdgm-rev__buyer-badge'));
    node.dataset.verifiedBuyer = String(verified);
    node.classList.toggle('abr-review--verified', verified);
    node.classList.remove('abr-review--rating-only');
    node.hidden = false;
    node.removeAttribute('aria-hidden');

    const pair = (selector, apply) => {
      const target = node.querySelector(selector);
      if (target) apply(target, raw.querySelector(selector));
    };
    pair('.jdgm-rev__title', (target, source) => { target.textContent = source?.textContent.trim() || ''; });
    pair('.jdgm-rev__body', (target, source) => { target.innerHTML = source?.innerHTML || ''; });
    pair('.jdgm-rev__author', (target, source) => { target.textContent = source?.textContent.trim() || abrText('anonymous', 'Anonymous'); });
    pair('.jdgm-rev__location', (target, source) => { target.textContent = source?.textContent.trim() || ''; });
    pair('.jdgm-rev__icon', (target, source) => {
      // Initials are recomputed from the name by the widget.
      delete target.dataset.abrInitials;
      if (source) target.textContent = source.textContent.trim();
    });
    pair('.jdgm-rev__rating', (target, source) => {
      const score = Number.parseFloat(source?.dataset.score || source?.getAttribute('aria-label')) || 0;
      target.dataset.score = String(score);
      target.setAttribute('aria-label', `${score} star review`);
      delete target.dataset.abrRating;
      target.querySelectorAll('.jdgm-star').forEach((star, index) => {
        star.classList.remove('jdgm--on', 'jdgm--off', 'jdgm--half');
        star.classList.add(score >= index + 1 ? 'jdgm--on' : score >= index + 0.5 ? 'jdgm--half' : 'jdgm--off');
      });
    });
    pair('.jdgm-rev__timestamp', (target, source) => {
      const content = source?.dataset.content || '';
      target.dataset.content = content;
      target.textContent = relativeTime(parseTime(content));
    });
    // Media, replies and the like: this review's own, or none.
    // Only blocks with real content: the raw markup carries empty ones
    // (e.g. a store-reply box) that Judge.me's script hides on the page -
    // copied as they were, they showed up as empty gray bars.
    const meaningful = (element) => Boolean(element && (
      element.textContent.replace(/[\s\u200B-\u200F\u2060\uFEFF]/g, '') ||
      element.querySelector('img, video, iframe, source')
    ));
    ['.jdgm-rev__pics', '.jdgm-rev__vids', '.jdgm-rev__reply', '.jdgm-rev__custom-form', '.jdgm-rev__actions'].forEach((selector) => {
      const target = node.querySelector(selector);
      const found = raw.querySelector(selector);
      const source = meaningful(found) ? found : null;
      if (target && source) target.replaceWith(document.importNode(source, true));
      else if (target) target.remove();
      else if (source) node.querySelector('.jdgm-rev__content')?.append(document.importNode(source, true));
    });
    return node;
  }

  // ---- Keeping the list one height -----------------------------------------
  //
  // Five reviews per page still vary a little in height (one line of text
  // or two), which moved the page numbers and everything below. Every
  // page is measured offscreen and the list holds the tallest one.

  // ---- Paging -------------------------------------------------------------

  function sortKey() {
    const select = liveRoot()?.querySelector('.jdgm-rev-widg__sort-wrapper .jdgm-sort-dropdown, .jdgm-sort-dropdown:not([name="reviewer_name_format"])');
    return (select?.selectedOptions?.[0]?.textContent || '').trim().toLowerCase();
  }

  function visibleReviews() {
    const verifiedOnly = widget()?.dataset.abrVerifiedOnly === 'true';
    const list = reviews.filter((review) =>
      review.shown &&
      (!verifiedOnly || review.verified) &&
      (!ratingFilter || Math.round(review.rating) === Number(ratingFilter))
    );
    const byRecent = (a, b) => (b.time - a.time) || (a.index - b.index);
    const key = sortKey();
    if (key === 'highest rating') list.sort((a, b) => (b.rating - a.rating) || byRecent(a, b));
    else if (key === 'lowest rating') list.sort((a, b) => (a.rating - b.rating) || byRecent(a, b));
    else list.sort(byRecent);
    return list;
  }

  function pageLink(number, label, extra = '') {
    const link = document.createElement('a');
    link.href = '#';
    link.className = `jdgm-paginate__page abr-page ${extra}`.trim();
    link.dataset.page = String(number);
    link.textContent = label;
    link.setAttribute('aria-label', abrText('page', 'Page [page]', { page: number }));
    return link;
  }

  function pageArrow(number, label, direction, disabled) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'jdgm-paginate__page abr-page abr-page--arrow';
    button.dataset.page = String(number);
    button.disabled = disabled;
    button.setAttribute('aria-label', label);
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.6');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('d', direction === 'previous' ? 'M10 3.5 5.5 8 10 12.5' : 'M6 3.5 10.5 8 6 12.5');
    svg.append(path);
    button.append(svg);
    return button;
  }

  function renderPagination(root, count) {
    const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
    let paginate = root.querySelector('.jdgm-paginate');
    if (!paginate) {
      paginate = document.createElement('div');
      paginate.className = 'jdgm-paginate';
      root.querySelector('.jdgm-rev-widg__reviews')?.insertAdjacentElement('afterend', paginate);
    }
    paginate.dataset.abrOwn = 'true';
    paginate.classList.add('abr-pagination');
    paginate.setAttribute('role', 'navigation');
    paginate.setAttribute('aria-label', abrText('pages_label', 'Review pages'));
    const controls = document.createElement('div');
    controls.className = 'abr-pagination__controls';
    if (pages > 1) {
      controls.append(pageArrow(Math.max(1, page - 1), abrText('previous_page', 'Previous review page'), 'previous', page === 1));
      const start = Math.max(1, Math.min(page - 1, pages - 2));
      const end = Math.min(pages, start + 2);
      for (let n = start; n <= end; n += 1) {
        const link = pageLink(n, String(n), n === page ? 'jdgm-curt' : '');
        if (n === page) link.setAttribute('aria-current', 'page');
        controls.append(link);
      }
      controls.append(pageArrow(Math.min(pages, page + 1), abrText('next_page', 'Next review page'), 'next', page === pages));
    }
    const position = document.createElement('p');
    position.className = 'abr-pagination__position';
    position.textContent = abrText('page_of', 'Page [page] of [pages]', { page, pages });
    paginate.replaceChildren(controls, position);
    paginate.hidden = pages <= 1;

  }

  // The first render swaps Judge.me's own cards for the same reviews from
  // the whole set, often while their entrance (adobe-reviews-alignment-fix-v2.css)
  // is playing or once it has: the new cards join that entrance at the
  // moment it started, so they carry on from where the old ones were, or
  // are simply there once it is over, instead of laying the list in again.
  const ENTRANCE = new Set(['abr-clip-in', 'abr-rise-sm']);
  const entrance = (container) => container.getAnimations({ subtree: true })
    .filter((animation) => ENTRANCE.has(animation.animationName) && animation.effect?.target !== container);

  // Before the swap (the old cards' animations end as they leave): when
  // the entrance started, 'over' once it has played, and null before it
  // plays or in the frame it starts (the new cards then start with it).
  function entranceOf(container) {
    if (!container.closest('.jdgm-rev-widg__body')?.hasAttribute('data-played')) return null;
    const animations = entrance(container);
    if (!animations.length) return 'over';
    return animations.find((animation) => animation.startTime !== null)?.startTime ?? null;
  }

  function carryEntrance(container, started) {
    if (started === null) return;
    entrance(container).forEach((animation) => {
      if (started === 'over') animation.finish();
      else animation.startTime = started;
    });
  }

  function emptyReviewState(root) {
    const matching = reviews.filter((review) =>
      (!ratingFilter || Math.round(review.rating) === Number(ratingFilter)) &&
      (widget()?.dataset.abrVerifiedOnly !== 'true' || review.verified)
    );
    const starOnly = matching.length > 0 && matching.every((review) => !review.shown);
    const card = document.createElement('div');
    card.className = 'abr-review-empty';
    card.setAttribute('role', 'status');
    const title = document.createElement('h3');
    title.className = 'abr-review-empty__title';
    title.textContent = starOnly
      ? (ratingFilter ? abrText('empty_star_only_stars', 'No written [stars]-star reviews yet', { stars: ratingFilter }) : abrText('empty_star_only', 'No written reviews yet'))
      : (ratingFilter ? abrText('empty_filter_stars', 'No [stars]-star reviews match this filter', { stars: ratingFilter }) : abrText('empty_filter', 'No reviews match this filter'));
    const text = document.createElement('p');
    text.className = 'abr-review-empty__text';
    text.textContent = starOnly
      ? abrText('empty_star_only_text', 'These ratings were submitted without written feedback, photos or videos. Star-only reviews are hidden from this list.')
      : abrText('empty_filter_text', 'Try another star rating or see all reviews.');
    card.append(title, text);
    if (ratingFilter) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'abr-review-empty__action';
      button.textContent = abrText('see_all', 'See all reviews');
      button.addEventListener('click', () => root.querySelector('.jdgm-histogram__clear-filter')?.click());
      card.append(button);
    }
    return card;
  }

  function render(carry = false, fromPage = null) {
    const root = liveRoot();
    const container = root?.querySelector('.jdgm-rev-widg__reviews');
    if (!root || !container || !reviews) return;
    const list = visibleReviews();
    const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    page = Math.min(Math.max(1, page), pages);
    const pageChanged = fromPage !== null && page !== fromPage;
    if (pageChanged) root.dispatchEvent(new CustomEvent('adobe-reviews:before-page', { bubbles: true }));
    container.style.removeProperty('min-height');
    const slice = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const started = carry ? entranceOf(container) : null;
    container.replaceChildren(...(list.length ? slice.map((review) => review.node.cloneNode(true)) : [emptyReviewState(root)]));
    carryEntrance(container, started);
    container.dataset.abrOwn = 'true';
    renderPagination(root, list.length);
    // Tell the widget's UI the page is in (it otherwise waits for the DOM
    // to go quiet, which a busy page may never do).
    root.dispatchEvent(new CustomEvent('adobe-reviews:rendered', { bubbles: true, detail: { pageChanged, page } }));
  }

  // ---- Taking over Judge.me's controls ------------------------------------

  // Capture on document: runs after the widget's own (visual) handlers,
  // which were registered first, and before Judge.me's, which it stops.
  document.addEventListener('click', (event) => {
    if (!takenOver) return;
    const control = event.target.closest?.('.jdgm-paginate__page, .jdgm-histogram__row[data-rating], .jdgm-histogram__star, .jdgm-histogram__clear-filter');
    if (!control || !control.closest('.jdgm-review-widget')) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    let fromPage = null;
    if (control.matches('.jdgm-paginate__page')) {
      if (control.disabled || control.getAttribute('aria-current') === 'page') return;
      fromPage = page;
      page = Number.parseInt(control.dataset.page, 10) || 1;
    } else if (control.matches('.jdgm-histogram__clear-filter')) {
      ratingFilter = null;
      page = 1;
    } else {
      const rating = control.closest('.jdgm-histogram__row')?.dataset.rating || null;
      ratingFilter = ratingFilter === rating ? null : rating;
      page = 1;
    }
    render(false, fromPage);
  }, true);

  document.addEventListener('change', (event) => {
    if (!takenOver) return;
    const select = event.target.closest?.('.jdgm-sort-dropdown:not([name="reviewer_name_format"])');
    if (!select || !select.closest('.jdgm-review-widget')) return;
    event.stopImmediatePropagation();
    // Stopping the event here also stops the sort menu's own listener on
    // the select, so its label and checkmark are updated here.
    const details = select.closest('.jdgm-rev-widg__sort-wrapper')?.querySelector('.abr-sort') || select.parentElement?.querySelector('.abr-sort');
    const label = details?.querySelector('.abr-sort__trigger > span');
    if (label) label.textContent = select.selectedOptions?.[0]?.textContent?.trim() || select.value;
    details?.querySelectorAll('.abr-sort__option[data-value]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.value === select.value));
    });
    page = 1;
    render();
  }, true);

  // ---- Start --------------------------------------------------------------

  async function load() {
    if (loading || takenOver) return;
    const root = liveRoot();
    const w = widget();
    const total = window.AdobeReviewsData?.reviewCount(w) || Number.parseInt(root?.dataset.numberOfReviews, 10) || 0;
    if (!root || !context.shop || !context.productId || total <= PAGE_SIZE) return;
    const knownIds = new Set(Array.from(root.querySelectorAll('.jdgm-rev[data-review-id]')).map((node) => node.dataset.reviewId));
    if (!knownIds.size) return;

    loading = true;
    try {
      const nodes = await fetchAll(total, knownIds);
      if (!nodes || nodes.length < knownIds.size) return;
      const template = templateFrom(root);
      if (!template) return;
      const finished = new Map(Array.from(root.querySelectorAll('.jdgm-rev[data-review-id]')).map((node) => [node.dataset.reviewId, node]));
      reviews = nodes.map((node, index) => {
        // Reviews Judge.me already finished on the page are used as they are.
        const own = finished.get(node.dataset.reviewId);
        const ready = own ? own.cloneNode(true) : build(node, template);
        ready.hidden = false;
        ready.removeAttribute('aria-hidden');
        wakeMedia(ready);
        return { ...describe(ready, index), shown: shown(ready) };
      });
      takenOver = true;
      w.dataset.abrAllReviews = 'true';
      page = 1;
      render(true);
      markUnwritten();
      // Verified only: re-page the whole set when it is toggled.
      new MutationObserver(() => {
        page = 1;
        render();
      }).observe(w, { attributes: true, attributeFilter: ['data-abr-verified-only'] });
      // If Judge.me re-renders its list (e.g. after a new review), put ours back.
      new MutationObserver(() => {
        const container = liveRoot()?.querySelector('.jdgm-rev-widg__reviews');
        if (container && !container.dataset.abrOwn) render();
      }).observe(w, { childList: true, subtree: true });
    } catch (error) {
      // Leave Judge.me's paging as it is.
    } finally {
      loading = false;
    }
  }

  function watch() {
    const w = widget();
    if (!w) return false;
    if (!('IntersectionObserver' in window)) {
      load();
      return true;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      // Judge.me may still be rendering; give it a moment.
      window.setTimeout(load, 300);
    }, { rootMargin: '1200px 0px' });
    observer.observe(w);
    return true;
  }

  // A product with star ratings but no written (or photo) reviews has nothing for
  // "What editors say" and the sort pill to head: the widget is marked
  // data-abr-unwritten and the stylesheet (adobe-reviews-alignment-fix-v2.css)
  // hides that row. Before the list is taken over, Judge.me's own nodes
  // decide (it renders the rating-only ones, then hides them).
  // Every .jdgm-rev-widg in the widget is marked from its own nodes, not
  // only the visible one: finding that needs layout, and this runs on every
  // widget change (the write-review modal included) where a forced layout
  // would start transitions the flow script means to skip.
  let marking = 0;
  function markUnwritten() {
    marking = 0;
    const w = widget();
    if (!w) return;
    w.querySelectorAll('.jdgm-rev-widg').forEach((root) => {
      let unwritten;
      if (takenOver && reviews) {
        unwritten = reviews.length > 0 && !reviews.some((review) => review.shown);
      } else {
        // only when this page is the whole list: a later page may hold
        // written reviews
        const nodes = [...root.querySelectorAll('.jdgm-rev')];
        const paged = root.querySelectorAll('.jdgm-paginate__page').length > 1;
        unwritten = !paged && nodes.length > 0 && !nodes.some(shown);
      }
      if (root.hasAttribute('data-abr-unwritten') !== unwritten) root.toggleAttribute('data-abr-unwritten', unwritten);
    });
  }

  function watchWritten() {
    const w = widget();
    if (!w) return false;
    markUnwritten();
    new MutationObserver((records) => {
      if (marking || records.every((record) => record.target.closest?.('.jdgm-write-review-modal'))) return;
      marking = requestAnimationFrame(markUnwritten);
    }).observe(w, { childList: true, subtree: true });
    return true;
  }

  function start() {
    if (!watchWritten()) {
      const waiting = new MutationObserver(() => {
        if (watchWritten()) waiting.disconnect();
      });
      waiting.observe(document.body, { childList: true, subtree: true });
    }
    if (watch()) return;
    const observer = new MutationObserver(() => {
      if (watch()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

